import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { FaTimes, FaExclamationTriangle, FaPlus } from 'react-icons/fa';
import { api } from '../../services/api';
import * as Sentry from "@sentry/react";
import { useAuth } from '../../components/auth/AuthContext';

// ============================================================================
// SENTRY LOGGING HELPERS
// ============================================================================
const logError = (message, context = {}, error = null) => {
  if (error) {
    Sentry.captureException(error, { 
      tags: { section: 'firewall-rule-modal' },
      extra: { ...context, message }
    });
  } else {
    Sentry.captureMessage(message, { 
      level: 'error',
      tags: { section: 'firewall-rule-modal' },
      extra: context 
    });
  }
};

const logInfo = (message, context = {}) => {
  Sentry.addBreadcrumb({
    message,
    category: 'firewall-rule-modal',
    level: 'info',
    data: context
  });
};

const logUserAction = (action, context = {}) => {
  Sentry.addBreadcrumb({
    message: `User action: ${action}`,
    category: 'user-interaction',
    level: 'info',
    data: context
  });
};

const logPerformance = (operation, duration, context = {}) => {
  Sentry.addBreadcrumb({
    message: `Performance: ${operation} took ${duration}ms`,
    category: 'performance',
    level: 'info',
    data: { ...context, duration }
  });
};

// ============================================================================
// ERROR MESSAGES MAPPING
// ============================================================================
const ERROR_MESSAGES = {
  'AUTH_ERROR': 'Please log in again to continue',
  'USER_NOT_FOUND': 'User profile not found. Please refresh and try again',
  'ACCESS_DENIED': 'You do not have permission to perform this action',
  'RATE_LIMIT_EXCEEDED': 'API limit reached. Try again after 60 seconds',
  'INVALID_USER_PROFILE': 'Invalid user profile format. Please contact support',
  'INCOMPLETE_PROFILE': 'Please complete your profile to continue',
  'SERVICE_CONFIG_ERROR': 'Service temporarily unavailable. Please try again later',
  'DB_ERROR': 'Service temporarily unavailable. Please try again later',
  'API_ERROR': 'Service temporarily unavailable. Please try again later',
  'INVALID_API_RESPONSE': 'Service error. Please try again later',
  'API_CONNECTION_ERROR': 'Unable to connect to service. Please check your connection and try again',
  'NETWORK_ERROR': 'Unable to connect to service. Please check your connection and try again',
  'INVALID_ID_FORMAT': 'Invalid request format. Please try again',
  'INVALID_DATA': 'Invalid input data. Please check your entries and try again',
  'INVALID_REQUEST': 'Invalid request format. Please try again',
  'MISSING_FIELDS': 'Please fill in all required fields',
  'VALIDATION_ERROR': 'Please check your input and try again',
  'INVALID_RULE_PARAMETERS': 'Invalid firewall rule parameters. Please check your input',
  'INVALID_JSON': 'Invalid request format. Please check your input',
  'UNEXPECTED_PAYLOAD': 'Unexpected request data. Please try again',
  'UNEXPECTED_BODY': 'Invalid request format. Please try again',
  'INVALID_RULE_ID_FORMAT': 'Invalid rule ID format. Please try again',
  'FIREWALL_LIST_FAILED': 'Unable to load firewalls. Please refresh the page',
  'FIREWALL_DETAILS_FAILED': 'Unable to load firewall details. Please try again',
  'FIREWALL_CREATE_FAILED': 'Unable to create firewall. Please try again',
  'FIREWALL_DELETE_FAILED': 'Unable to delete firewall. Please try again',
  'FIREWALL_NOT_FOUND': 'Firewall not found. It may have been deleted',
  'FIREWALL_LIMIT_EXCEEDED': 'You have reached the maximum limit of 50 firewalls',
  'FIREWALL_HAS_ATTACHMENTS': 'Firewall is attached to VMs. Please detach before deleting',
  'FIREWALL_IN_USE': 'Firewall is currently in use. Please detach all instances before deleting',
  'RESOURCE_ALREADY_EXISTS': 'A firewall with this name already exists',
  'FIREWALL_RULE_ADD_FAILED': 'Unable to add firewall rule. Please try again',
  'FIREWALL_RULE_DELETE_FAILED': 'Unable to remove firewall rule. Please try again',
  'FIREWALL_RULE_ALREADY_EXISTS': 'This firewall rule already exists',
  'FIREWALL_RULE_NOT_FOUND': 'Firewall rule not found. It may have been removed',
  'SECURITY_RULE_ADD_FAILED': 'Unable to add security rule. Please try again',
  'SECURITY_RULE_DELETE_FAILED': 'Unable to remove security rule. Please try again',
  'SECURITY_RULE_ALREADY_EXISTS': 'This security rule already exists for this instance',
  'RULE_NOT_FOUND': 'Security rule not found. It may have been removed',
  'VM_LIST_FAILED': 'Unable to load virtual machines. Please refresh the page',
  'VM_NOT_FOUND': 'Virtual machine not found. It may have been deleted',
  'VM_NOT_ACTIVE': 'Virtual machine must be active to perform this action',
  'VM_FIREWALLS_FAILED': 'Unable to load VM firewall information. Please try again',
  'VM_EVENTS_FAILED': 'Unable to load VM events. Please try again',
  'FIREWALL_ATTACH_FAILED': 'Unable to attach firewall to VMs. Please try again',
  'NO_VMS_SELECTED': 'Please select at least one virtual machine',
  'INVALID_VM_IDS': 'Some selected VMs are invalid or not found',
  'VM_NOT_COMPATIBLE': 'Selected VMs are not compatible with this firewall (different regions)',
  'INSTANCE_NOT_FOUND': 'Instance not found. It may have been deleted',
  'INSTANCE_NOT_AVAILABLE': 'Instance is not available for this operation',
  'INSTANCE_NOT_COMPATIBLE': 'Selected instances are not compatible with this firewall',
  'INSTANCES_NOT_ACTIVE': 'Some instances are not active. Only active instances can be modified',
  'INSTANCE_NOT_ACTIVE': 'Instance must be active to perform this action',
  'INVALID_INSTANCE_ID_FORMAT': 'Invalid instance ID format. Expected format: BRK-XXXXXXD',
  'TIER_NOT_SUPPORTED': 'This operation is only supported for legacy tier instances',
  'REGION_NOT_AVAILABLE': 'Selected region is not available. Please choose a different region',
  'INVALID_REGION_FORMAT': 'Invalid region format. Please try again',
  'UNSUPPORTED_REGION': 'This region is not supported for firewalls',
  'PROTOCOLS_FAILED': 'Unable to load supported protocols. Please refresh the page',
  'REGIONS_FAILED': 'Unable to load available regions. Please refresh the page'
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================




const getNetworkErrorMessage = (error) => {
  if (!navigator.onLine) {
    return {
      message: "You're offline. Check your connection.",
      code: 'OFFLINE'
    };
  }
  
  if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') {
    return {
      message: "Request timed out. Please try again.",
      code: 'TIMEOUT'
    };
  }
  
  if (error.code === 'ERR_NETWORK' && navigator.onLine) {
    return {
      message: "Unable to reach server. Please try later.",
      code: 'SERVER_UNREACHABLE'
    };
  }
  
  if (error.code === 'ERR_NETWORK' && !navigator.onLine) {
    return {
      message: "You're offline. Check your connection.",
      code: 'OFFLINE'
    };
  }
  
  if (error.request && !error.response) {
    return {
      message: "No response from server. Please try again.",
      code: 'NO_RESPONSE'
    };
  }
  
  return {
    message: "Something went wrong. Please try again.",
    code: 'UNKNOWN_NETWORK_ERROR'
  };
};

const sanitizeText = (text) => {
  if (!text || typeof text !== 'string') return '';
  return text
    .replace(/[<>&"']/g, (char) => {
      const entities = { '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&#x27;' };
      return entities[char];
    })
    .substring(0, 1000);
};

const getSafeErrorMessage = (error) => {
  if (!error || typeof error !== 'object') {
    return 'Something went wrong. Please try again.';
  }
  
  if (error.userMessage) {
    return error.userMessage;
  }
  
  if (error.error) {
    return error.error;
  }
  
  if (error.response?.data?.code && ERROR_MESSAGES[error.response.data.code]) {
    return ERROR_MESSAGES[error.response.data.code];
  }
  
  if (error.code && ERROR_MESSAGES[error.code]) {
    return ERROR_MESSAGES[error.code];
  }
  
  if (error.response?.status === 401 || error.status === 401) {
    return ERROR_MESSAGES['AUTH_ERROR'];
  }
  
  return 'Something went wrong. Please try again.';
};

const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(null, args), delay);
  };
};

// ============================================================================
// RESPONSIVE HOOK
// ============================================================================
const useResponsive = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);

  useEffect(() => {
    const checkDevice = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      setIsTablet(width >= 768 && width < 1024);
    };

    checkDevice();
    window.addEventListener('resize', checkDevice);
    return () => window.removeEventListener('resize', checkDevice);
  }, []);

  return { isMobile, isTablet };
};

// ============================================================================
// SHIELD ICON COMPONENT
// ============================================================================
const ShieldIcon = ({ size = 20, color = '#1a1a1a' }) => (
  <svg width={size} height={size + 4} viewBox="0 0 24 28" fill={color}>
    <path d="M12 0L0 4v10c0 6 5 12 12 14 7-2 12-8 12-14V4L12 0z"/>
  </svg>
);

// ============================================================================
// INLINE STYLES
// ============================================================================
const getStyles = (isSubmitting = false, direction = 'ingress') => ({
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.75)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 50,
    padding: '16px',
  },
  modal: {
    backgroundColor: '#121212',
    borderRadius: '14px',
    width: '100%',
    maxWidth: '640px',
    border: '1px solid #2a2a2a',
    overflow: 'hidden',
    display: 'flex',
    boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)',
  },
  leftPanel: {
    flex: 1,
    padding: '22px 24px',
    borderRight: '1px solid #2a2a2a',
    opacity: isSubmitting ? 0.6 : 1,
    transition: 'opacity 0.15s',
    pointerEvents: isSubmitting ? 'none' : 'auto',
  },
  rightPanel: {
    width: '200px',
    padding: '22px 20px',
    backgroundColor: '#0a0a0a',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '20px',
  },
  iconWrapper: {
    width: '42px',
    height: '42px',
    borderRadius: '10px',
    background: isSubmitting 
      ? 'linear-gradient(135deg, #3a3a3a 0%, #2a2a2a 100%)'
      : 'linear-gradient(135deg, #e0eacc 0%, #b8d4a0 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: '12px',
    boxShadow: isSubmitting ? 'none' : '0 4px 12px rgba(224,234,204,0.3)',
    transition: 'all 0.15s',
  },
  title: {
    color: 'white',
    margin: 0,
    fontSize: '17px',
    fontWeight: '600',
  },
  subtitle: {
    color: '#6b7280',
    margin: 0,
    fontSize: '12px',
  },
  row: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '14px',
    marginBottom: '14px',
  },
  label: {
    color: '#9ca3af',
    fontSize: '11px',
    fontWeight: '500',
    marginBottom: '4px',
    display: 'block',
  },
  required: {
    color: '#ff6b6b',
    marginLeft: '2px',
  },
  dropdown: {
    position: 'relative',
  },
  dropdownTrigger: {
    width: '100%',
    padding: '10px 12px',
    backgroundColor: isSubmitting ? '#151515' : '#1a1a1a',
    border: '1px solid #333',
    borderRadius: '8px',
    color: isSubmitting ? '#666' : 'white',
    fontSize: '13px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    cursor: isSubmitting ? 'not-allowed' : 'pointer',
    boxSizing: 'border-box',
    transition: 'all 0.15s',
  },
  dropdownMenu: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    marginTop: '4px',
    backgroundColor: '#1a1a1a',
    border: '1px solid #333',
    borderRadius: '8px',
    zIndex: 100,
    overflow: 'hidden',
  },
  dropdownItem: {
    padding: '10px 12px',
    color: 'white',
    fontSize: '13px',
    cursor: 'pointer',
    transition: 'background 0.15s',
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    backgroundColor: isSubmitting ? '#151515' : '#1a1a1a',
    border: '1px solid #333',
    borderRadius: '8px',
    color: isSubmitting ? '#666' : 'white',
    fontSize: '13px',
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'all 0.15s',
  },
  errorBox: {
    backgroundColor: 'rgba(239, 68, 68, 0.1)',
    border: '1px solid rgba(239, 68, 68, 0.3)',
    borderRadius: '8px',
    padding: '10px 12px',
    marginBottom: '16px',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
  },
  errorText: {
    color: '#ff6b6b',
    fontSize: '10px',
    marginTop: '4px',
  },
  summaryHeader: {
    color: '#6b7280',
    fontSize: '10px',
    letterSpacing: '1.5px',
    margin: '0 0 16px 0',
  },
  summaryLabel: {
    color: '#6b7280',
    fontSize: '10px',
    margin: '0 0 4px 0',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  summaryValue: {
    color: 'white',
    fontSize: '13px',
    margin: '0 0 12px 0',
    wordBreak: 'break-all',
  },
  directionBadge: {
    display: 'inline-block',
    padding: '4px 10px',
    borderRadius: '6px',
    fontSize: '10px',
    fontWeight: '600',
    marginRight: '6px',
    backgroundColor: direction === 'ingress' ? 'rgba(59, 130, 246, 0.15)' : 'rgba(234, 179, 8, 0.15)',
    color: direction === 'ingress' ? '#60a5fa' : '#fcd34d',
    border: `1px solid ${direction === 'ingress' ? 'rgba(59, 130, 246, 0.4)' : 'rgba(234, 179, 8, 0.4)'}`,
  },
  protocolBadge: {
    display: 'inline-block',
    padding: '4px 10px',
    borderRadius: '6px',
    fontSize: '10px',
    fontWeight: '600',
    backgroundColor: 'rgba(224,234,204,0.15)',
    color: '#e0eacc',
    border: '1px solid rgba(224,234,204,0.4)',
  },
  submitBtn: (disabled) => ({
    width: '100%',
    padding: '12px 16px',
    backgroundColor: disabled ? '#3d4435' : '#e0eacc',
    color: disabled ? '#a0a89a' : 'black',
    border: 'none',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: disabled ? 'not-allowed' : 'pointer',
    boxShadow: disabled ? 'none' : '0 4px 14px rgba(224,234,204,0.25)',
    marginTop: 'auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    transition: 'all 0.15s',
  }),
  cancelBtn: (disabled) => ({
    width: '100%',
    marginTop: '10px',
    padding: '10px 16px',
    backgroundColor: '#333',
    color: disabled ? '#666' : 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '13px',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1,
    transition: 'all 0.15s',
  }),
  spinner: {
    width: '14px',
    height: '14px',
    border: '2px solid rgba(0,0,0,0.2)',
    borderTopColor: 'black',
    borderRadius: '50%',
    animation: 'spin 0.7s linear infinite',
  },
});

// ============================================================================
// MAIN COMPONENT
// ============================================================================
const FirewallRuleModal = ({ isOpen, onClose, firewall, onRuleAdded }) => {
  const { handleAuthError } = useAuth();
  const { isMobile, isTablet } = useResponsive();
  
  // Form states
  const [direction, setDirection] = useState('ingress');
  const [protocol, setProtocol] = useState('tcp');
  const [ethertype, setEthertype] = useState('IPv4');
  const [remoteIpPrefix, setRemoteIpPrefix] = useState('0.0.0.0/0');
  const [portRangeMin, setPortRangeMin] = useState('');
  const [portRangeMax, setPortRangeMax] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [protocols, setProtocols] = useState([
    { id: 'tcp', name: 'TCP' },
    { id: 'udp', name: 'UDP' },
    { id: 'icmp', name: 'ICMP' }
  ]);
  
  // Dropdown state
  const [openDropdown, setOpenDropdown] = useState(null);
  
  // Validation states
  const [ipPrefixError, setIpPrefixError] = useState('');
  const [portRangeError, setPortRangeError] = useState('');
  
  // Refs
  const abortControllerRef = useRef(null);
  const dropdownRef = useRef(null);

  
  // Derived values
  const styles = getStyles(loading, direction);
  const isMobileOrTablet = isMobile || isTablet;
  
  // Memoized validation check
  const canSubmit = useMemo(() => {
    return !loading && !ipPrefixError && !portRangeError && remoteIpPrefix.trim();
  }, [loading, ipPrefixError, portRangeError, remoteIpPrefix]);

  // Sentry context
  useEffect(() => {
    if (isOpen && firewall) {
      Sentry.setContext("firewall-rule-modal", {
        firewallId: firewall.id,
        firewallName: firewall.name,
        direction,
        protocol,
        ethertype,
        remoteIpPrefix,
        portRangeMin,
        portRangeMax,
        hasValidationErrors: !!(ipPrefixError || portRangeError),
        isLoading: loading,
        isMobile,
        isTablet
      });
    }
  }, [isOpen, firewall, direction, protocol, ethertype, remoteIpPrefix, portRangeMin, portRangeMax, protocols.length, ipPrefixError, portRangeError, loading, isMobile, isTablet]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        logInfo('Cleanup: Aborted pending firewall rule modal requests');
      }
    };
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDropdown(null);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);



  // Reset form
  const resetForm = useCallback(() => {
    logInfo('Firewall rule form reset');
    setDirection('ingress');
    setProtocol('tcp');
    setEthertype('IPv4');
    setRemoteIpPrefix('0.0.0.0/0');
    setPortRangeMin('');
    setPortRangeMax('');
    setIpPrefixError('');
    setPortRangeError('');
    setError(null);
    setOpenDropdown(null);
  }, []);

 useEffect(() => {
  if (isOpen && firewall) {
    logInfo('Firewall rule modal opened', {
      firewallId: firewall.id,
      firewallName: firewall.name,
      isMobile,
      isTablet
    });

    logUserAction('firewall_rule_modal_opened', {
      firewallId: firewall.id,
      firewallName: firewall.name
    });

    resetForm();
  }
}, [isOpen, firewall, resetForm, isMobile, isTablet]);
  // IP prefix validation
  const validateIpPrefix = useCallback((value) => {
    if (!value || typeof value !== 'string') {
      return 'Remote IP prefix is required';
    }
    
    const sanitizedValue = sanitizeText(value);
    
    if (ethertype === 'IPv4') {
      const ipv4Regex = /^(\d{1,3}\.){3}\d{1,3}\/\d{1,2}$/;
      if (!ipv4Regex.test(sanitizedValue)) {
        return 'Invalid IPv4 CIDR format. Example: 192.168.1.0/24';
      }
      
      const parts = sanitizedValue.split('/');
      const ipParts = parts[0].split('.');
      
      for (const part of ipParts) {
        const num = parseInt(part, 10);
        if (num < 0 || num > 255) {
          return 'IPv4 address parts must be between 0 and 255';
        }
      }
      
      const prefix = parseInt(parts[1], 10);
      if (isNaN(prefix) || prefix < 0 || prefix > 32) {
        return 'IPv4 CIDR prefix must be between 0 and 32';
      }
    } else if (ethertype === 'IPv6') {
      const ipv6Regex = /^([0-9a-fA-F]{0,4}:){2,7}[0-9a-fA-F]{0,4}\/\d{1,3}$/;
      if (!ipv6Regex.test(sanitizedValue) && sanitizedValue !== '::/0') {
        return 'Invalid IPv6 CIDR format';
      }
    }
    
    return '';
  }, [ethertype]);

  // Port range validation
  const validatePortRange = useCallback((min, max) => {
    if ((min && !max) || (!min && max)) {
      return 'Both minimum and maximum port must be specified';
    }
    
    if (min && max) {
      const minPort = parseInt(sanitizeText(min), 10);
      const maxPort = parseInt(sanitizeText(max), 10);
      
      if (isNaN(minPort) || isNaN(maxPort)) {
        return 'Port values must be numeric';
      }
      
      if (minPort < 1 || minPort > 65535) {
        return 'Minimum port must be between 1 and 65535';
      }
      
      if (maxPort < 1 || maxPort > 65535) {
        return 'Maximum port must be between 1 and 65535';
      }
      
      if (minPort > maxPort) {
        return 'Minimum port must be less than or equal to maximum port';
      }
    }
    
    return '';
  }, []);

  // Handlers
  const handleRemoteIpPrefixChange = useCallback((e) => {
    const value = e.target.value;
    setRemoteIpPrefix(value);
    setError(null);
    
    const validationError = validateIpPrefix(value);
    setIpPrefixError(validationError);

    logUserAction('firewall_rule_ip_prefix_changed', {
      firewallId: firewall?.id,
      ethertype,
      hasError: !!validationError
    });
  }, [validateIpPrefix, ethertype, firewall?.id]);

  const handlePortRangeMinChange = useCallback((e) => {
    const value = e.target.value;
    setPortRangeMin(value);
    setError(null);
    
    const validationError = validatePortRange(value, portRangeMax);
    setPortRangeError(validationError);

    logUserAction('firewall_rule_port_min_changed', {
      firewallId: firewall?.id,
      protocol
    });
  }, [portRangeMax, validatePortRange, protocol, firewall?.id]);

  const handlePortRangeMaxChange = useCallback((e) => {
    const value = e.target.value;
    setPortRangeMax(value);
    setError(null);
    
    const validationError = validatePortRange(portRangeMin, value);
    setPortRangeError(validationError);

    logUserAction('firewall_rule_port_max_changed', {
      firewallId: firewall?.id,
      protocol
    });
  }, [portRangeMin, validatePortRange, protocol, firewall?.id]);

  const toggleDropdown = useCallback((name) => {
    const newState = openDropdown === name ? null : name;
    
    logUserAction(`firewall_rule_${name}_dropdown_toggled`, {
      firewallId: firewall?.id,
      opened: newState === name,
      dropdownName: name
    });
    
    setOpenDropdown(newState);
  }, [openDropdown, firewall?.id]);

  const handleDirectionSelect = useCallback((value) => {
    logUserAction('firewall_rule_direction_selected', {
      firewallId: firewall?.id,
      previousDirection: direction,
      newDirection: value
    });
    
    setDirection(value);
    setOpenDropdown(null);
    setError(null);
  }, [direction, firewall?.id]);

  const handleProtocolSelect = useCallback((value) => {
    logUserAction('firewall_rule_protocol_selected', {
      firewallId: firewall?.id,
      previousProtocol: protocol,
      newProtocol: value
    });
    
    setProtocol(value);
    setOpenDropdown(null);
    setError(null);
    
    if (value !== 'tcp' && value !== 'udp') {
      setPortRangeMin('');
      setPortRangeMax('');
      setPortRangeError('');
    }
  }, [protocol, firewall?.id]);

  const handleEthertypeSelect = useCallback((value) => {
    logUserAction('firewall_rule_ethertype_selected', {
      firewallId: firewall?.id,
      previousEthertype: ethertype,
      newEthertype: value
    });
    
    setEthertype(value);
    setOpenDropdown(null);
    setRemoteIpPrefix(value === 'IPv4' ? '0.0.0.0/0' : '::/0');
    setIpPrefixError('');
    setError(null);
  }, [ethertype, firewall?.id]);

  const getProtocolDisplayName = useCallback(() => {
    const protocolObj = protocols.find(p => p.id === protocol);
    return protocolObj ? sanitizeText(protocolObj.name) : 'Select protocol';
  }, [protocol, protocols]);

const handleSubmit = useCallback(async () => {
  const startTime = performance.now();
  
  logUserAction('firewall_rule_creation_submitted', {
    firewallId: firewall?.id,
    direction,
    protocol,
    ethertype,
    hasPortRange: !!(portRangeMin && portRangeMax)
  });
  
  const ipError = validateIpPrefix(remoteIpPrefix);
  const portError = validatePortRange(portRangeMin, portRangeMax);
  
  if (ipError) {
    setIpPrefixError(ipError);
    setError(ipError);
    logError('Firewall rule creation failed - IP prefix validation error', {
      firewallId: firewall?.id,
      error: ipError
    });
    return;
  }
  
  if (portError) {
    setPortRangeError(portError);
    setError(portError);
    logError('Firewall rule creation failed - port range validation error', {
      firewallId: firewall?.id,
      error: portError
    });
    return;
  }
  
  if (abortControllerRef.current) {
    abortControllerRef.current.abort();
  }
  
  abortControllerRef.current = new AbortController();
  
  setLoading(true);
  setError(null);
  
  const ruleData = {
    direction: sanitizeText(direction),
    protocol: sanitizeText(protocol),
    ethertype: sanitizeText(ethertype),
    remote_ip_prefix: sanitizeText(remoteIpPrefix)
  };
  
  if (portRangeMin && portRangeMax) {
    ruleData.port_range_min = parseInt(sanitizeText(portRangeMin), 10);
    ruleData.port_range_max = parseInt(sanitizeText(portRangeMax), 10);
  }
  
  try {
    const response = await api.addFirewallRule(firewall.id, ruleData);
    const endTime = performance.now();
    const duration = endTime - startTime;

    if (abortControllerRef.current?.signal.aborted) {
      logInfo('Firewall rule creation request was aborted', { duration });
      return;
    }
    
    if (response.success) {
      if (onRuleAdded) onRuleAdded();
      onClose();

      logPerformance('create_firewall_rule', duration, {
        firewallId: firewall.id,
        direction,
        protocol,
        success: true
      });

      logInfo('Firewall rule created successfully', {
        firewallId: firewall.id,
        ruleId: response.rule?.id,
        direction,
        protocol,
        duration: `${duration.toFixed(2)}ms`
      });

      logUserAction('firewall_rule_creation_success', {
        firewallId: firewall.id,
        ruleId: response.rule?.id
      });
    } else {
      const safeMessage = getSafeErrorMessage(response);
      setError(safeMessage);
      
      logError('Failed to create firewall rule', {
        firewallId: firewall.id,
        code: response.code,
        ruleData,
        duration: `${duration.toFixed(2)}ms`
      });
    }
  } catch (err) {
    if (err.name === 'AbortError') {
      logInfo('Firewall rule creation request aborted by user');
      return;
    }
    
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    // Position 1: Authentication error
    if (err.status === 401 || err.code === 'AUTH_ERROR') {
      logError('Authentication error during firewall rule creation', {
        firewallId: firewall.id,
        code: err.code,
        duration: `${duration.toFixed(2)}ms`
      });
      handleAuthError();
      return;
    }
    
    // Position 2: Use ERROR_MESSAGES lookup if code exists and has mapping
    if (err.code && ERROR_MESSAGES[err.code]) {
      setError(ERROR_MESSAGES[err.code]);
      logError('Firewall rule creation error', {
        firewallId: firewall.id,
        code: err.code,
        status: err.status,
        duration: `${duration.toFixed(2)}ms`
      }, err);
      return;
    }
    
    // Position 3: Use interceptor's user-friendly message directly
    if (err.error) {
      setError(err.error);
      logError('Firewall rule creation error', {
        firewallId: firewall.id,
        code: err.code,
        status: err.status,
        message: err.error,
        duration: `${duration.toFixed(2)}ms`
      }, err);
      return;
    }
    
    // Position 4: Fallback for unexpected error format
    setError('Something went wrong. Please try again.');
    logError('Firewall rule creation error (unexpected format)', {
      firewallId: firewall.id,
      ruleData,
      errorType: typeof err,
      duration: `${duration.toFixed(2)}ms`
    }, err);
  } finally {
    setLoading(false);
  }
}, [
  validateIpPrefix,
  validatePortRange,
  remoteIpPrefix,
  portRangeMin,
  portRangeMax,
  direction,
  protocol,
  ethertype,
  firewall,
  onRuleAdded,
  onClose,
  handleAuthError
]);
  // Close handler
  const handleClose = useCallback(() => {
    if (loading) return;
    
    logUserAction('firewall_rule_modal_closed', {
      firewallId: firewall?.id,
      direction,
      protocol
    });
    
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    onClose();
  }, [firewall?.id, direction, protocol, onClose, loading]);

  // Get modal styles based on device
  const getModalStyles = useCallback(() => {
    if (isMobile) {
      return {
        width: 'calc(100% - 1rem)',
        maxWidth: '100%',
        maxHeight: '90vh',
        margin: '0.5rem',
        flexDirection: 'column',
      };
    } else if (isTablet) {
      return {
        width: 'calc(100% - 2rem)',
        maxWidth: '560px',
        maxHeight: '85vh',
        margin: '1rem',
        flexDirection: 'column',
      };
    }
    return {};
  }, [isMobile, isTablet]);

  if (!isOpen) return null;

  const firewallName = firewall?.name || '';
  const modalStyles = getModalStyles();

  return (
    <>
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        .firewall-rule-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .firewall-rule-scrollbar::-webkit-scrollbar-track {
          background: #2a2a2a;
          border-radius: 3px;
        }
        .firewall-rule-scrollbar::-webkit-scrollbar-thumb {
          background: #555;
          border-radius: 3px;
        }
        .firewall-rule-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #666;
        }
      `}</style>
      
      <div style={styles.overlay}>
        <div style={{ ...styles.modal, ...modalStyles }} ref={dropdownRef}>
          {/* Mobile/Tablet Header */}
          {isMobileOrTablet && (
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '14px 18px',
              borderBottom: '1px solid #2a2a2a',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{
                  width: '38px',
                  height: '38px',
                  borderRadius: '10px',
                  background: 'linear-gradient(135deg, #e0eacc 0%, #b8d4a0 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <ShieldIcon size={18} color="#1a1a1a" />
                </div>
                <div>
                  <h2 style={{ color: 'white', margin: 0, fontSize: '16px', fontWeight: '600' }}>Add Rule</h2>
                  <p style={{ color: '#6b7280', margin: 0, fontSize: '11px' }}>{sanitizeText(firewallName)}</p>
                </div>
              </div>
              <button
                onClick={handleClose}
                disabled={loading}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#6b7280',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  padding: '6px',
                  opacity: loading ? 0.5 : 1,
                }}
              >
                <FaTimes size={18} />
              </button>
            </div>
          )}

          {/* LEFT PANEL - Form */}
          <div style={{
            ...styles.leftPanel,
            borderRight: isMobileOrTablet ? 'none' : '1px solid #2a2a2a',
            padding: isMobileOrTablet ? '18px' : '22px 24px',
            overflowY: isMobileOrTablet ? 'auto' : 'visible',
            maxHeight: isMobileOrTablet ? 'calc(90vh - 70px)' : 'none',
          }} className="firewall-rule-scrollbar">
            {/* Desktop Header */}
            {!isMobileOrTablet && (
              <div style={styles.header}>
                <div style={styles.iconWrapper}>
                  <ShieldIcon size={20} color={loading ? '#666' : '#1a1a1a'} />
                </div>
                <div>
                  <h2 style={styles.title}>Add Firewall Rule</h2>
                  <p style={styles.subtitle}>Configure traffic rule for {sanitizeText(firewallName)}</p>
                </div>
              </div>
            )}

            {/* Error Display */}
            {error && (
              <div style={styles.errorBox}>
                <FaExclamationTriangle size={14} style={{ color: '#ef4444', marginTop: '1px', flexShrink: 0 }} />
                <span style={{ color: '#fca5a5', fontSize: '12px' }}>{sanitizeText(error)}</span>
              </div>
            )}

            {/* Row 1: Direction + Protocol */}
            <div style={isMobileOrTablet ? { display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '12px' } : styles.row}>
              <div>
                <label style={styles.label}>Direction<span style={styles.required}>*</span></label>
                <div style={styles.dropdown}>
                  <div
                    style={{
                      ...styles.dropdownTrigger,
                      borderColor: openDropdown === 'direction' ? '#e0eacc' : '#333'
                    }}
                    onClick={() => !loading && toggleDropdown('direction')}
                    onMouseEnter={(e) => { if (!loading) e.currentTarget.style.borderColor = '#e0eacc'; }}
                    onMouseLeave={(e) => { if (!loading && openDropdown !== 'direction') e.currentTarget.style.borderColor = '#333'; }}
                  >
                    <span>{direction === 'ingress' ? 'Ingress' : 'Egress'}</span>
                    <span style={{ color: '#6b7280', fontSize: '10px' }}>▼</span>
                  </div>
                  {openDropdown === 'direction' && (
                    <div style={styles.dropdownMenu}>
                      <div
                        style={{ ...styles.dropdownItem, backgroundColor: direction === 'ingress' ? '#252525' : 'transparent' }}
                        onClick={() => handleDirectionSelect('ingress')}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#252525'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = direction === 'ingress' ? '#252525' : 'transparent'}
                      >
                        Ingress
                      </div>
                      <div
                        style={{ ...styles.dropdownItem, backgroundColor: direction === 'egress' ? '#252525' : 'transparent' }}
                        onClick={() => handleDirectionSelect('egress')}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#252525'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = direction === 'egress' ? '#252525' : 'transparent'}
                      >
                        Egress
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              <div>
                <label style={styles.label}>Protocol<span style={styles.required}>*</span></label>
                <div style={styles.dropdown}>
                  <div
                    style={{
                      ...styles.dropdownTrigger,
                      borderColor: openDropdown === 'protocol' ? '#e0eacc' : '#333'
                    }}
                    onClick={() => !loading && toggleDropdown('protocol')}
                    onMouseEnter={(e) => { if (!loading) e.currentTarget.style.borderColor = '#e0eacc'; }}
                    onMouseLeave={(e) => { if (!loading && openDropdown !== 'protocol') e.currentTarget.style.borderColor = '#333'; }}
                  >
                    <span>{getProtocolDisplayName()}</span>
                    <span style={{ color: '#6b7280', fontSize: '10px' }}>▼</span>
                  </div>
                  {openDropdown === 'protocol' && (
                    <div style={styles.dropdownMenu}>
                      {protocols.map(p => (
                        <div
                          key={p.id}
                          style={{ ...styles.dropdownItem, backgroundColor: protocol === p.id ? '#252525' : 'transparent' }}
                          onClick={() => handleProtocolSelect(p.id)}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#252525'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = protocol === p.id ? '#252525' : 'transparent'}
                        >
                          {sanitizeText(p.name)}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Row 2: IP Version + Remote IP */}
            <div style={isMobileOrTablet ? { display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '12px' } : styles.row}>
              <div>
                <label style={styles.label}>IP Version<span style={styles.required}>*</span></label>
                <div style={styles.dropdown}>
                  <div
                    style={{
                      ...styles.dropdownTrigger,
                      borderColor: openDropdown === 'ethertype' ? '#e0eacc' : '#333'
                    }}
                    onClick={() => !loading && toggleDropdown('ethertype')}
                    onMouseEnter={(e) => { if (!loading) e.currentTarget.style.borderColor = '#e0eacc'; }}
                    onMouseLeave={(e) => { if (!loading && openDropdown !== 'ethertype') e.currentTarget.style.borderColor = '#333'; }}
                  >
                    <span>{ethertype}</span>
                    <span style={{ color: '#6b7280', fontSize: '10px' }}>▼</span>
                  </div>
                  {openDropdown === 'ethertype' && (
                    <div style={styles.dropdownMenu}>
                      <div
                        style={{ ...styles.dropdownItem, backgroundColor: ethertype === 'IPv4' ? '#252525' : 'transparent' }}
                        onClick={() => handleEthertypeSelect('IPv4')}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#252525'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = ethertype === 'IPv4' ? '#252525' : 'transparent'}
                      >
                        IPv4
                      </div>
                      <div
                        style={{ ...styles.dropdownItem, backgroundColor: ethertype === 'IPv6' ? '#252525' : 'transparent' }}
                        onClick={() => handleEthertypeSelect('IPv6')}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#252525'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = ethertype === 'IPv6' ? '#252525' : 'transparent'}
                      >
                        IPv6
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              <div>
                <label style={styles.label}>Remote IP<span style={styles.required}>*</span></label>
                <input
                  type="text"
                  value={remoteIpPrefix}
                  onChange={handleRemoteIpPrefixChange}
                  disabled={loading}
                  style={{
                    ...styles.input,
                    borderColor: ipPrefixError ? '#ff6b6b' : '#333',
                  }}
                  placeholder={ethertype === 'IPv4' ? '0.0.0.0/0' : '::/0'}
                  onFocus={(e) => {
                    if (!loading) {
                      e.target.style.borderColor = '#e0eacc';
                      e.target.style.backgroundColor = '#252525';
                      e.target.style.boxShadow = '0 0 0 3px rgba(224, 234, 204, 0.15)';
                    }
                    logUserAction('firewall_rule_ip_prefix_field_focused', {
                      firewallId: firewall?.id,
                      ethertype
                    });
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = ipPrefixError ? '#ff6b6b' : '#333';
                    e.target.style.backgroundColor = '#1a1a1a';
                    e.target.style.boxShadow = 'none';
                    logUserAction('firewall_rule_ip_prefix_field_blurred', {
                      firewallId: firewall?.id,
                      ethertype,
                      hasError: !!ipPrefixError
                    });
                  }}
                />
                {ipPrefixError && <div style={styles.errorText}>{sanitizeText(ipPrefixError)}</div>}
              </div>
            </div>

            {/* Row 3: Port Min + Port Max */}
            {(protocol === 'tcp' || protocol === 'udp') && (
              <div style={isMobileOrTablet ? { display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '12px' } : styles.row}>
                <div>
                  <label style={styles.label}>Port Min</label>
                  <input
                    type="number"
                    value={portRangeMin}
                    onChange={handlePortRangeMinChange}
                    disabled={loading}
                    min="1"
                    max="65535"
                    style={{
                      ...styles.input,
                      borderColor: portRangeError ? '#ff6b6b' : '#333',
                    }}
                    placeholder="1"
                    onFocus={(e) => {
                      if (!loading) {
                        e.target.style.borderColor = '#e0eacc';
                        e.target.style.backgroundColor = '#252525';
                        e.target.style.boxShadow = '0 0 0 3px rgba(224, 234, 204, 0.15)';
                      }
                      logUserAction('firewall_rule_port_min_field_focused', {
                        firewallId: firewall?.id,
                        protocol
                      });
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = portRangeError ? '#ff6b6b' : '#333';
                      e.target.style.backgroundColor = '#1a1a1a';
                      e.target.style.boxShadow = 'none';
                      logUserAction('firewall_rule_port_min_field_blurred', {
                        firewallId: firewall?.id,
                        protocol,
                        hasError: !!portRangeError
                      });
                    }}
                  />
                </div>
                <div>
                  <label style={styles.label}>Port Max</label>
                  <input
                    type="number"
                    value={portRangeMax}
                    onChange={handlePortRangeMaxChange}
                    disabled={loading}
                    min="1"
                    max="65535"
                    style={{
                      ...styles.input,
                      borderColor: portRangeError ? '#ff6b6b' : '#333',
                    }}
                    placeholder="65535"
                    onFocus={(e) => {
                      if (!loading) {
                        e.target.style.borderColor = '#e0eacc';
                        e.target.style.backgroundColor = '#252525';
                        e.target.style.boxShadow = '0 0 0 3px rgba(224, 234, 204, 0.15)';
                      }
                      logUserAction('firewall_rule_port_max_field_focused', {
                        firewallId: firewall?.id,
                        protocol
                      });
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = portRangeError ? '#ff6b6b' : '#333';
                      e.target.style.backgroundColor = '#1a1a1a';
                      e.target.style.boxShadow = 'none';
                      logUserAction('firewall_rule_port_max_field_blurred', {
                        firewallId: firewall?.id,
                        protocol,
                        hasError: !!portRangeError
                      });
                    }}
                  />
                </div>
              </div>
            )}
            {portRangeError && (protocol === 'tcp' || protocol === 'udp') && (
              <div style={{ ...styles.errorText, marginTop: '-8px', marginBottom: '12px' }}>{sanitizeText(portRangeError)}</div>
            )}

            {/* Mobile/Tablet: Summary + Buttons */}
            {isMobileOrTablet && (
              <div style={{ marginTop: '16px' }}>
                {/* Rule Preview */}
                <div style={{
                  padding: '14px',
                  backgroundColor: '#0a0a0a',
                  borderRadius: '10px',
                  marginBottom: '16px',
                }}>
                  <h4 style={{ color: '#6b7280', fontSize: '10px', letterSpacing: '1px', margin: '0 0 10px 0' }}>RULE PREVIEW</h4>
                  <div style={{ display: 'flex', gap: '6px', marginBottom: '8px' }}>
                    <span style={styles.directionBadge}>{direction.toUpperCase()}</span>
                    <span style={styles.protocolBadge}>{protocol.toUpperCase()}</span>
                  </div>
                  <p style={{ color: '#9ca3af', fontSize: '12px', margin: 0, fontFamily: 'monospace' }}>
                    {remoteIpPrefix} → Ports {portRangeMin || '*'}-{portRangeMax || '*'}
                  </p>
                </div>

                <button
                  onClick={handleSubmit}
                  disabled={!canSubmit}
                  style={styles.submitBtn(!canSubmit)}
                >
                  {loading ? (
                    <>
                      <div style={styles.spinner} />
                      <span>Adding...</span>
                    </>
                  ) : (
                    <>
                      <FaPlus size={12} />
                      <span>Add Rule</span>
                    </>
                  )}
                </button>
                <button
                  onClick={handleClose}
                  disabled={loading}
                  style={styles.cancelBtn(loading)}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>

          {/* RIGHT PANEL - Summary (Desktop Only) */}
          {!isMobileOrTablet && (
            <div style={styles.rightPanel}>
              <h3 style={styles.summaryHeader}>RULE PREVIEW</h3>
              
              <div style={{ marginBottom: '14px' }}>
                <span style={styles.directionBadge}>{direction.toUpperCase()}</span>
                <span style={styles.protocolBadge}>{protocol.toUpperCase()}</span>
              </div>
              
              <p style={styles.summaryLabel}>IP Version</p>
              <p style={{ ...styles.summaryValue, color: '#e0eacc' }}>{ethertype}</p>
              
              <p style={styles.summaryLabel}>Remote IP</p>
              <p style={{ ...styles.summaryValue, fontFamily: 'monospace', fontSize: '12px' }}>{remoteIpPrefix}</p>
              
              {(protocol === 'tcp' || protocol === 'udp') && (
                <>
                  <p style={styles.summaryLabel}>Ports</p>
                  <p style={styles.summaryValue}>
                    {portRangeMin && portRangeMax ? `${portRangeMin} - ${portRangeMax}` : 'All'}
                  </p>
                </>
              )}
              
              <div style={{ flex: 1 }} />
              
              <button
                onClick={handleSubmit}
                disabled={!canSubmit}
                style={styles.submitBtn(!canSubmit)}
              >
                {loading ? (
                  <>
                    <div style={styles.spinner} />
                    <span>Adding...</span>
                  </>
                ) : (
                  <>
                    <FaPlus size={12} />
                    <span>Add Rule</span>
                  </>
                )}
              </button>
              <button
                onClick={handleClose}
                disabled={loading}
                style={styles.cancelBtn(loading)}
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default FirewallRuleModal;